import { findCompany, findStructure, useStore_client_access } from '~/stores/client/access'
import { equals, head, length, Maybe, notEmpty, pipe_, propOr, values } from '~/utils/lib/core/prelude'
import { extractRouteCompanyCode, storeNavigationCompany, storeNavigationStructure, stripCompanyQueryFromRoute } from '~/stores/layout/navigation'

/**
 * @desc Middleware for routes that require a `?company=` query.
 * If missing/invalid, redirect to company selector with referer in query.
 */
export function enforceCompanyRequiredScope(/** @type {import('~/utils/lib/core/types_').Route_} */ to) {
  const structure_id = propOr('', 'dataId', to.params)
  const company_code = extractRouteCompanyCode(to)
  const hasCompanyQueryParam = Object
    .prototype
    .hasOwnProperty
    .call(to.query ?? {}, 'company')
  const accessData = useStore_client_access().data
  const mStructure = findStructure(accessData, structure_id)

  if (hasCompanyQueryParam && !notEmpty(company_code)) {
    const normalizedRoute = stripCompanyQueryFromRoute(to)

    if (normalizedRoute !== to.fullPath)
      return navigateTo(normalizedRoute, { replace: true })
  }

  /// (should not happened: will currently break the experience)
  if (Maybe.isNothing(mStructure))
    return

  const structure = Maybe.fromJust(mStructure)

  /// a) With valid company provided
  ///
  if (notEmpty(company_code)) {
    const mCompany = findCompany(structure, company_code)

    if (Maybe.isJust(mCompany))
      return
  }

  /// b) No company, but current data only owns **one** company
  ///    (automatic selection pattern)
  ///

  /** @type {import('~/utils/lib/core/prelude_').Maybe<import('~/stores/client/access').Company>} */
  // @ts-ignore: UNSAFE coerce
  const firstCompanyFound = pipe_(
    values(structure.societes),
    head,
    Maybe.toMaybe
  )

  const companyCount = pipe_(
    values(structure.societes),
    length
  )


  if
    (
      Maybe.isJust(firstCompanyFound) &&
      equals(companyCount, 1)
    ) {
      const companyCode = Maybe.fromJust(firstCompanyFound).code_societe

      const hydratedPath = hydrateWithCompanyParams(
        to,
        structure_id,
        companyCode
      )

      if (hydratedPath === to.fullPath)
        return

      return navigateTo(hydratedPath, { replace: true })
    }


  /// c) No company, accepting navigation to the user company selection page
  ///
  return navigateTo(
    {
      path: `/data/${structure_id}/user-company-selection`,
      query: {
        referer: to.fullPath
      }
    },
    {
      replace: true
    }
  )
}

export default defineNuxtRouteMiddleware((to) => {
  return enforceCompanyRequiredScope(to)
})

function hydrateWithCompanyParams(
  /** @type {import('~/utils/lib/core/types_').Route_} */ route,
  /** @type {import('~/stores/client/access').Structure["id"]} */ dataId,
  /** @type {import('~/stores/client/access').Company["code_societe"]} */ companyCode,
) {
  const redirectURL = getSafeRedirectURL(route, dataId)
  const [path, search] = redirectURL.split('?')
  const query = new URLSearchParams(search ?? '')

  query.set('company', companyCode)

  storeNavigationStructure(dataId)
  storeNavigationCompany(companyCode)

  return `${path}?${query.toString()}`
}

function getSafeRedirectURL(
  /** @type {import('~/utils/lib/core/types_').Route_} */ route,
  /** @type {import('~/stores/client/access').Structure["id"]} */ dataId,
) {
  const fallback = notEmpty(route.fullPath) ?
    route.fullPath :
    `/data/${dataId}/dashboard`

  if (!fallback.startsWith(`/data/${dataId}/`))
    return `/data/${dataId}/dashboard`

  return fallback
}

